import React, { FC, RefObject, useEffect, useRef, useState } from 'react'
import {
	RadioList,
	RequestFormPDFProps,
	RequestInputFiles,
	RequestInputs,
	ChildInformation
} from '@utils/request'
import requestForm from '@components/requestForms/request.form'
import { Collapse } from 'react-collapse'
import {
	REQUEST_CONFIGS_KEYS,
	REQUEST_TN_DETAILS_STEPS_NAME,
	requestCategories,
	Direction
} from '@services/constants'
import * as pageUtils from './__forms.utils'
import { useAppSelector } from '@services/store'
import TextInput from './inputs/text.inputs'
import { joinClasses, makeClasses } from '@utils/styles'
import { Colors } from '@utils/css-variables'
import RequestFiles from '@components/ui/requestFiles'
import { formatStrapiText, getFilesFromStore } from '@utils/methods'
import RadioInput from '@components/requestForms/inputs/radio.input'
import Notification from '@components/ui/notification'
import * as formUtils from '@pages/auth/__index.utils'
import MaskedTextInput from './inputs/textWithValidation.inputs'
import { PHONE_NUMBER_REGEX } from '@pages/auth/profile/__index.utils'
import SelectInput from './inputs/select.inputs'
import MapForm from '@components/ui/map/MapForm'

const LeisureTarifsReductionForm: FC<RequestFormPDFProps> = ({
	inputs,
	errors,
	subSteps,
	isSummaryStep,
	steps,
	toPrint,
	filesInputs,
	onSetInputFiles,
	onSetSubSteps,
	onFixError,
	onSetInputs,
	setCurrentStep,
	setRequiredFieldAttributes
}) => {
	const {
		authUser,
		pageAssets,
		UpdateRequestInputsArray,
		UpdateRequestInputFilesArray,
		UpdateRequestInputsErrorsArray
	} = requestForm()

	type Classes = {
		button: string
		outlinedButton: string
		btnPadding: string
		greyBackgroundBlock: string
	}

	const classes: Classes = makeClasses({
		button: {
			display: 'inline-block',
			padding: '10px 20px',
			fontSize: '13px',
			fontWeight: '500',
			color: Colors.white,
			lineHeight: '1em',
			borderRadius: '23px',
			transition: 'all 0.35s ease',
			MsTransition: 'all 0.35s ease',
			background: Colors.secondary,
			border: 'none',
			cursor: 'pointer',
			'&:hover': {
				background: Colors.darkBlue2,
				borderColor: Colors.darkBlue2,
				color: Colors.white
			}
		},
		outlinedButton: {
			background: 'none',
			border: `1px solid ${Colors.secondary} !important`,
			color: Colors.secondary
		},

		btnPadding: {
			padding: '10px 20px !important',
			fontSize: '13px'
		},
		greyBackgroundBlock: {
			background: Colors.darkWhite,
			padding: '15px 30px'
		}
	})

	const answers = useAppSelector((state) => state.request.configs)

	//----------------------------------------------------------------
	//----------------------- RADIO LISTS -------------------------
	//----------------------------------------------------------------
	// Radio lists used to populate RadioInput and SelectInput components below.
	// Keep these close to the top so they can be referenced by label helpers
	// (getLabelFromList) and by the initial state logic.

	const radioListGrantForWhom: RadioList[] = [
		{
			label: pageAssets.request_form_leisureTarifs_grantForWhom_user_label,
			value: REQUEST_CONFIGS_KEYS.carSharingForWhom.firstOption,
			key: 'request_form_leisureTarifs_grantForWhom_user_label'
		},
		{
			label:
				pageAssets.request_form_leisureTarifs_grantForWhom_beneficiary_label,
			value: REQUEST_CONFIGS_KEYS.carSharingForWhom.secondOption,
			key: 'request_form_leisureTarifs_grantForWhom_beneficiary_label',
			extraText:
				pageAssets.request_form_leisureTarifs_grantForWhom_beneficiary_label_extraText
		}
	]

	const radioListMaritalStatus: RadioList[] = [
		{
			label: pageAssets?.request_form_leisureTarifs_maritalStatus_option1_label,
			value: REQUEST_CONFIGS_KEYS.maritalStatus.single,
			key: 'request_form_leisureTarifs_maritalStatus_option1_label'
		},
		{
			label: pageAssets?.request_form_leisureTarifs_maritalStatus_option2_label,
			value: REQUEST_CONFIGS_KEYS.maritalStatus.married,
			key: 'request_form_leisureTarifs_maritalStatus_option2_label'
		},
		{
			label: pageAssets?.request_form_leisureTarifs_maritalStatus_option3_label,
			value: REQUEST_CONFIGS_KEYS.maritalStatus.separated,
			key: 'request_form_leisureTarifs_maritalStatus_option3_label'
		},
		{
			label: pageAssets?.request_form_leisureTarifs_maritalStatus_option4_label,
			value: REQUEST_CONFIGS_KEYS.maritalStatus.divorced,
			key: 'request_form_leisureTarifs_maritalStatus_option4_label'
		},
		{
			label: pageAssets?.request_form_leisureTarifs_maritalStatus_option5_label,
			value: REQUEST_CONFIGS_KEYS.maritalStatus.widowed,
			key: 'request_form_leisureTarifs_maritalStatus_option5_label'
		}
	]

	const radioListChildrenCount: RadioList[] = [
		{
			label: pageAssets?.request_form_leisureTarifs_childrenCount_option1_label,
			value: REQUEST_CONFIGS_KEYS.childCount.none,
			key: 'request_form_leisureTarifs_childrenCount_option1_label'
		},
		{
			label: '1',
			value: REQUEST_CONFIGS_KEYS.childCount.one,
			key: 'request_form_leisureTarifs_childrenCount_option3_label'
		},
		{
			label: '2',
			value: REQUEST_CONFIGS_KEYS.childCount.two,
			key: 'request_form_leisureTarifs_childrenCount_option4_label'
		},
		{
			label: '3',
			value: REQUEST_CONFIGS_KEYS.childCount.three,
			key: 'request_form_leisureTarifs_childrenCount_option5_label'
		},
		{
			label: '4',
			value: REQUEST_CONFIGS_KEYS.childCount.four,
			key: 'request_form_leisureTarifs_childrenCount_option6_label'
		},
		{
			label: '5',
			value: REQUEST_CONFIGS_KEYS.childCount.five,
			key: 'request_form_leisureTarifs_childrenCount_option7_label'
		},
		{
			label: pageAssets?.request_form_leisureTarifs_childrenCount_option2_label,
			value: REQUEST_CONFIGS_KEYS.childCount.sixOrMore,
			key: 'request_form_leisureTarifs_childrenCount_option2_label'
		}
	]

	const radioListGender: RadioList[] = [
		{ label: '', value: '', key: '' },
		{
			label: pageAssets?.label_woman,
			value: REQUEST_CONFIGS_KEYS.genderSelect.female,
			key: 'label_woman'
		},
		{
			label: pageAssets?.label_man,
			value: REQUEST_CONFIGS_KEYS.genderSelect.male,
			key: 'label_man'
		},
		{
			label: pageAssets?.request_form_septicTank_projectType_option4,
			value: REQUEST_CONFIGS_KEYS.genderSelect.other,
			key: 'request_form_septicTank_projectType_option4'
		},
		{
			label: pageAssets?.label_prefer_not_to_choose,
			value: REQUEST_CONFIGS_KEYS.genderSelect.noAnswer,
			key: 'label_prefer_not_to_choose'
		}
	]

	//----------------------------------------------------------------
	//-------------------- DESCRIPTION INPUTS ------------------------
	//----------------------------------------------------------------
	// Main RequestInputs (text/select/number) managed locally in this component.
	// Each RequestInputs state mirrors a field that will be sent to the parent
	// request form manager via onSetInputs. They typically include a `ref`,
	// localized label and a `required` flag that can change dynamically.

	// Pour qui faites-vous cette demande?
	const [grantForWhomInput, setGrantForWhomInput] = useState<RequestInputs>({
		name: 'grantForWhom',
		label: pageAssets?.request_form_sustainableHygiene_forWhom_label,
		labelKey: 'request_form_sustainableHygiene_forWhom_label',
		value: answers.grantForWhom?.toString() ?? '',
		required: true,
		ref: useRef<HTMLInputElement>(null)
	})

	// SECTION A
	const [maritalStatusInput, setMaritalStatusInput] = useState<RequestInputs>({
		name: 'maritalStatus',
		label: pageAssets?.request_form_leisureTarifs_maritalStatus_label,
		labelKey: 'request_form_leisureTarifs_maritalStatus_label',
		value: answers.maritalStatus?.toString() ?? '',
		required: true,
		ref: useRef<HTMLInputElement>(null)
	})

	const [childrenCountInput, setChildrenCountInput] = useState<RequestInputs>({
		name: 'childrenCount',
		label: pageAssets?.request_form_leisureTarifs_childrenCount_label,
		labelKey: 'request_form_leisureTarifs_childrenCount_label',
		value: answers.childrenCount?.toString() ?? '',
		required: true,
		ref: useRef<HTMLInputElement>(null)
	})

	// SECTION A1
	const [beneficiaryFirstNameInput, setBeneficiaryFirstNameInput] =
		useState<RequestInputs>({
			name: 'beneficiaryFirstName',
			label: pageAssets?.request_form_sustainableHygiene_beneficiary_firstName,
			labelKey: 'request_form_sustainableHygiene_beneficiary_firstName',
			value: answers.beneficiaryFirstName || '',
			required:
				answers?.grantForWhom?.toString() ==
				radioListGrantForWhom[1].value,
			ref: useRef<HTMLInputElement>(null)
		})

	const [beneficiaryLastNameInput, setBeneficiaryLastNameInput] =
		useState<RequestInputs>({
			name: 'beneficiaryLastName',
			label: pageAssets?.request_form_sustainableHygiene_beneficiary_lastName,
			labelKey: 'request_form_sustainableHygiene_beneficiary_lastName',
			value: answers.beneficiaryLastName || '',
			required:
				answers?.grantForWhom?.toString() ==
				radioListGrantForWhom[1].value,
			ref: useRef<HTMLInputElement>(null)
		})

	const [beneficiaryAddressInput, setBeneficiaryAddressInput] =
		useState<RequestInputs>({
			name: 'beneficiaryAddress',
			label: pageAssets?.request_form_sustainableHygiene_beneficiary_address,
			labelKey: 'request_form_sustainableHygiene_beneficiary_address',
			value: answers.beneficiaryAddress || '',
			required:
				answers?.grantForWhom?.toString() ==
				radioListGrantForWhom[1].value,
			ref: useRef<HTMLInputElement>(null)
		})

	const [beneficiaryAppartmentInput, setBeneficiaryAppartmentInput] =
		useState<RequestInputs>({
			name: 'beneficiaryAppartment',
			label: pageAssets?.apartment_label,
			labelKey: 'apartment_label',
			value: answers.beneficiaryAppartment || '',
			required: false,
			ref: useRef<HTMLInputElement>(null)
		})

	const [beneficiaryEmailInput, setBeneficiaryEmailInput] =
		useState<RequestInputs>({
			name: 'beneficiaryEmail',
			label: pageAssets?.label_email,
			labelKey: 'label_email',
			value: answers.beneficiaryEmail || '',
			required:
				answers?.grantForWhom?.toString() ==
				radioListGrantForWhom[1].value,
			ref: useRef<HTMLInputElement>(null)
		})

	const [beneficiaryPhoneNumberInput, setBeneficiaryPhoneNumberInput] =
		useState<RequestInputs>({
			name: 'beneficiaryPhoneNumber',
			label: pageAssets?.label_telephone,
			labelKey: 'label_telephone',
			value: answers.beneficiaryPhoneNumber || '',
			required:
				answers?.grantForWhom?.toString() ==
				radioListGrantForWhom[1].value,
			ref: useRef<HTMLInputElement>(null)
		})

	const [beneficiaryAgeInput, setBeneficiaryAgeInput] = useState<RequestInputs>(
		{
			name: 'beneficiaryAge',
			label: pageAssets?.label_age,
			labelKey: 'label_telephone',
			value: answers.beneficiaryAge?.toString() || '',
			required: true,
			ref: useRef<HTMLInputElement>(null)
		}
	)

	const [beneficiarySexInput, setBeneficiarySexInput] = useState<RequestInputs>(
		{
			name: 'beneficiarySex',
			label: pageAssets?.label_sex,
			labelKey: 'label_sex',
			value: answers.beneficiarySex?.toString() || '',
			required: false,
			ref: useRef<HTMLInputElement>(null)
		}
	)

	const [spouseFirstNameInput, setSpouseFirstNameInput] =
		useState<RequestInputs>({
			name: 'spouseFirstName',
			label: pageAssets?.request_form_sustainableHygiene_beneficiary_firstName,
			labelKey: 'request_form_sustainableHygiene_beneficiary_firstName',
			value: answers.spouseFirstName || '',
			required: false,
			ref: useRef<HTMLInputElement>(null)
		})

	const [spouseLastNameInput, setSpouseLastNameInput] = useState<RequestInputs>(
		{
			name: 'spouseLastName',
			label: pageAssets?.request_form_sustainableHygiene_beneficiary_lastName,
			labelKey: 'request_form_sustainableHygiene_beneficiary_lastName',
			value: answers.spouseLastName || '',
			required: false,
			ref: useRef<HTMLInputElement>(null)
		}
	)

	const [spouseEmailInput, setSpouseEmailInput] = useState<RequestInputs>({
		name: 'spouseEmail',
		label: pageAssets?.label_email,
		labelKey: 'label_email',
		value: answers.spouseEmail || '',
		required: false,
		ref: useRef<HTMLInputElement>(null)
	})

	const [spousePhoneNumberInput, setSpousePhoneNumberInput] =
		useState<RequestInputs>({
			name: 'spousePhoneNumber',
			label: pageAssets?.label_telephone,
			labelKey: 'label_telephone',
			value: answers.spousePhoneNumber || '',
			required: false,
			ref: useRef<HTMLInputElement>(null)
		})

	const [spouseAgeInput, setSpouseAgeInput] = useState<RequestInputs>({
		name: 'spouseAge',
		label: pageAssets?.label_age,
		labelKey: 'label_telephone',
		value: answers.spouseAge?.toString() || '',
		required: false,
		ref: useRef<HTMLInputElement>(null)
	})

	const [spouseSexInput, setSpouseSexInput] = useState<RequestInputs>({
		name: 'spouseSex',
		label: pageAssets?.label_sex,
		labelKey: 'label_sex',
		value: answers.spouseSex?.toString() || '',
		required: false,
		ref: useRef<HTMLInputElement>(null)
	})

	const requiredAttributeNewChild = ['firstName', 'lastName', 'age']

	const [childAttributes, setChildAttributes] = useState<string[][]>(
		answers?.childAttributes ?? [requiredAttributeNewChild]
	)

	const initialChildInfo = {
		firstName: '',
		lastName: '',
		age: '',
		gender: ''
	}

	const [childInformationList, setChildInformationList] = useState<
		ChildInformation[]
	>(answers?.childInformationList ?? [])

	// a fake RequestInputs to put childInformationList data in
	// value will be encoded as JSON
	const [childInformationListInput, setChildInformationListInput] =
		useState<RequestInputs>({
			name: 'childInformationList',
			label:
				pageAssets?.request_form_annualPermitAndTagForAnimals_65_years_older,
			labelKey: 'request_form_annualPermitAndTagForAnimals_65_years_older',
			value: JSON.stringify(childInformationList),
			required: true,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1
		})

	const [childAttributesInput, setChildAttributesInput] =
		useState<RequestInputs>({
			name: 'childAttributes',
			label: '',
			labelKey: '',
			value: JSON.stringify(childAttributes),
			required: true,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1
		})

	const childrenCountMax = () => {
		switch (childrenCountInput.value) {
			case REQUEST_CONFIGS_KEYS.childCount.one:
				return 1
			case REQUEST_CONFIGS_KEYS.childCount.two:
				return 2
			case REQUEST_CONFIGS_KEYS.childCount.three:
				return 3
			case REQUEST_CONFIGS_KEYS.childCount.four:
				return 4
			case REQUEST_CONFIGS_KEYS.childCount.five:
				return 5
			case REQUEST_CONFIGS_KEYS.childCount.sixOrMore:
				return 8
			default:
				return 0
		}
	}

	const onSetChildInformationInput = (
		value: string,
		currentInput: { row: number; attribute: string }
	) => {
		const { row, attribute } = currentInput

		let itemList = structuredClone(childInformationList)

		if (attribute === 'age' && value.length > 2) {
			return
		}
		itemList[row][`${attribute}`] = value

		setChildInformationList(itemList)

		setChildInformationListInput({
			...childInformationListInput,
			value: JSON.stringify(itemList)
		})

		onFixError(updateChildInformationErrorsArray(itemList))
	}

	const doesChildInformationHasErrors = (row: number): boolean => {
		const listErrors: string[] = []
		const childInforList =
			row === undefined ? childInformationList : [childInformationList[row]]

		if (childInforList) {
			for (const element of childInforList) {
				const attrs = childAttributes[row] ?? []
				for (const attr of attrs) {
					if (element[`${attr}`] == '') {
						listErrors.push(`childInformation_${attr}${row}`)
					}
				}
			}
		}

		// Verify if it has errors and dispatch an action to set the various error messages
		if (listErrors.length > 0) {
			onFixError(listErrors)
		}

		return listErrors.length > 0
	}

	const addChildInformationRow = () => {
		if (doesChildInformationHasErrors(childInformationList.length - 1)) return
		let itemList = structuredClone(childInformationList)

		if (itemList.length > childrenCountMax() - 1) {
			return
		}
		let newItem: ChildInformation = initialChildInfo

		itemList = itemList?.map((item: ChildInformation) => {
			return {
				...item,
				display_information: false
			}
		})

		itemList.push(newItem)

		setChildInformationList(itemList)

		setChildInformationListInput({
			...childInformationListInput,
			value: JSON.stringify(itemList)
		})

		// reset required attr
		updateChildAttribute([...childAttributes, requiredAttributeNewChild])

		// =================================================================

		onFixError(updateChildInformationErrorsArray(itemList))
	}

	const removeChildInformationRow = (row: number) => {
		let itemList = [...childInformationList]

		itemList.splice(row, 1)

		setChildInformationList(itemList)

		setChildInformationListInput({
			...childInformationListInput,
			value: JSON.stringify(itemList)
		})

		// =================================================================

		// update RequiredAttributes
		let requiredAttributes = [...childAttributes]

		requiredAttributes.splice(row, 1)

		updateChildAttribute(requiredAttributes)

		// =================================================================

		onFixError(
			updateChildInformationRowErrorsArray([
				`childInformation_firstName${row}`,
				`childInformation_lastName${row}`,
				`childInformation_age${row}`,
				`childInformation_gender${row}`
			])
		)
	}

// Child information helpers
// - childAttributes: arrays describing which child fields are required for each row
// - childInformationList: array of ChildInformation objects stored as JSON in a
//   fake RequestInputs entry so the parent form receives it as a single value.
// The functions below add/remove rows and validate the per-row required fields.

	const updateChildAttribute = (list: string[][]) => {
		setChildAttributes(list)

		setChildAttributesInput({
			...childAttributesInput,
			value: JSON.stringify(list)
		})

		setRequiredFieldAttributes?.(list)
	}

	const updateChildInformationErrorsArray = (
		itemList: ChildInformation[]
	): string[] => {
		let errorArray: string[] = []

		errors?.forEach((error) => {
			if (error.includes('childInformation')) {
				itemList.forEach((childInformation, row) => {
					if (
						childInformation.firstName == '' &&
						error == `childInformation_firstName${row}`
					) {
						errorArray.push(error)
					}
					if (
						childInformation.lastName == '' &&
						error == `childInformation_lastName${row}`
					) {
						errorArray.push(error)
					}
					if (
						childInformation.age == '' &&
						error == `childInformation_age${row}`
					) {
						errorArray.push(error)
					}
				})
			} else {
				errorArray.push(error)
			}
		})

		return errorArray
	}

	const updateChildInformationRowErrorsArray = (
		inputNameList: string[]
	): string[] => {
		return errors.filter((err) => !inputNameList.includes(err))
	}

	//----------------------------------------------------------------
	//----------------------- DOCUMENTS INPUTS -----------------------
	//----------------------------------------------------------------
	// File inputs (RequestInputFiles) are stored locally as sets of File objects
	// and synchronized with the parent using onSetInputFiles. They also carry
	// metadata (category, required, numberFilesRequired) used by the
	// RequestFiles component for validation and display.

	// Preuve de résidence
	const [proofOfResidencyInput, setproofOfResidencyInput] =
		useState<RequestInputFiles>({
			name: 'proofOfResidency',
			label:
				pageAssets?.request_form_heritageBuildingsRevitalizationProgram_ninthInput_label,
			labelKey:
				'request_form_heritageBuildingsRevitalizationProgram_ninthInput_label',
			description:
				pageAssets?.request_form_tarifs_reduction_proofOfResidency_description,
			files: new Set<File>(
				filesInputs?.find((el) => el.name == 'proofOfResidency')?.files || []
			),
			required: false,
			numberFilesRequired: 1,
			category: requestCategories.proofOfResidency,
			shouldValidateIn: REQUEST_TN_DETAILS_STEPS_NAME.DOCUMENTS,
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DOCUMENTS
		})

	// Photo(s) ou autre(s) document(s) pertinent(s)
	const [optionalFilesInput, setOptionalFilesInput] =
		useState<RequestInputFiles>({
			name: 'optionalFilesInput',
			label: pageAssets?.request_form_leisureTarifs_optional_label,
			labelKey: 'request_form_leisureTarifs_optional_label',
			description:
				pageAssets?.request_form_tarifs_reduction_optionalFile_description,
			files: new Set<File>(
				filesInputs?.find((el) => el.name == 'optionalFilesInput')?.files || []
			),
			required: false,
			numberMaxFiles: 5,
			category: requestCategories.other
		})

	// Photo(s) ou autre(s) document(s) pertinent(s)
	const [beneficiaryFilesInput, setBeneficiaryFilesInput] =
		useState<RequestInputFiles>({
			name: 'beneficiaryFilesInput',
			label: pageAssets?.request_form_leisureTarifs_assessment_notice_label,
			labelKey: 'request_form_claimNotice_firstInput_label',
			description:
				pageAssets?.request_form_leisureTarifs_assessment_notice_description_label,
			files: new Set<File>(
				filesInputs?.find((el) => el.name == 'beneficiaryFilesInput')?.files ||
					[]
			),
			required: true,
			numberFilesRequired: 1,
			category: requestCategories.noticeOfContribution,
			shouldValidateIn: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1,
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DOCUMENTS
		})

	const [spouseFilesInput, setSpouseFilesInput] = useState<RequestInputFiles>({
		name: 'spouseFilesInput',
		label: pageAssets?.request_form_leisureTarifs_assessment_notice_label,
		labelKey: 'request_form_claimNotice_firstInput_label',
		description:
			pageAssets?.request_form_leisureTarifs_assessment_notice_description_label,
		files: new Set<File>(
			filesInputs?.find((el) => el.name == 'spouseFilesInput')?.files || []
		),
		required: answers?.maritalStatus == Number(radioListMaritalStatus[1].value),
		numberFilesRequired: 1,
		category: requestCategories.noticeOfContribution,
		shouldValidateIn: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1,
		subStep: REQUEST_TN_DETAILS_STEPS_NAME.DOCUMENTS
	})

	// ------------- DETAILS INPUTS SETTERS ----------------

	const onSelectGrantForWhomInput = (value: string) => {
		let requiredInput = true
		let user = authUser?.profile
		setGrantForWhomInput({ ...grantForWhomInput, value })

		if (value === radioListGrantForWhom[0].value) {
			requiredInput = false
		}

		setBeneficiaryFirstNameInput({
			...beneficiaryFirstNameInput,
			value: requiredInput ? '' : user?.firstName!,
			required: requiredInput
		})
		setBeneficiaryLastNameInput({
			...beneficiaryLastNameInput,
			value: requiredInput ? '' : user?.lastName!,
			required: requiredInput
		})

		setBeneficiaryAddressInput({
			...beneficiaryAddressInput,
			value: '',
			required: requiredInput
		})
		setBeneficiaryAppartmentInput({ ...beneficiaryAppartmentInput, value: '' })

		setBeneficiaryEmailInput({
			...beneficiaryEmailInput,
			value: requiredInput ? '' : user?.email!,
			required: requiredInput
		})

		setBeneficiaryPhoneNumberInput({
			...beneficiaryPhoneNumberInput,
			value: requiredInput ? '' : user?.phoneNumber!,
			required: requiredInput
		})

		onFixError(
			errors.filter(
				(error) =>
					![
						grantForWhomInput.name,
						beneficiaryFirstNameInput.name,
						beneficiaryLastNameInput.name,
						beneficiaryAddressInput.name,
						beneficiaryEmailInput.name,
						beneficiaryPhoneNumberInput.name
					].includes(error)
			)
		)
	}

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, grantForWhomInput))
	}, [grantForWhomInput])

	// SECTION A
	const onSelectMaritalStatusInput = (value: string) => {
		let requiredInput = false
		setMaritalStatusInput({
			...maritalStatusInput,
			value: `${value}`
		})

		if (value === radioListMaritalStatus[1].value) {
			requiredInput = true
		}

		setSpouseFirstNameInput({
			...spouseFirstNameInput,
			required: requiredInput
		})
		setSpouseLastNameInput({
			...spouseLastNameInput,
			required: requiredInput
		})

		setSpouseEmailInput({
			...spouseEmailInput,
			required: requiredInput
		})

		setSpousePhoneNumberInput({
			...spousePhoneNumberInput,
			required: requiredInput
		})

		setSpouseAgeInput({
			...spouseAgeInput,
			required: requiredInput
		})
		setSpouseFilesInput({
			...spouseFilesInput,
			required: requiredInput
		})

		onFixError(
			errors.filter(
				(error) =>
					![
						maritalStatusInput.name,
						spouseFirstNameInput.name,
						spouseLastNameInput.name,
						spouseEmailInput.name,
						spousePhoneNumberInput.name,
						spouseAgeInput.name,
						spouseFilesInput.name
					].includes(error)
			)
		)
	}

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, maritalStatusInput))
	}, [maritalStatusInput])

	const onSelectChildrenCountInput = (value: string) => {
		setChildrenCountInput({
			...childrenCountInput,
			value: `${value}`
		})

		onFixError(UpdateRequestInputsErrorsArray(errors, childrenCountInput.name))
	}

	useEffect(() => {
		if (
			childrenCountInput.value !==
				REQUEST_CONFIGS_KEYS.childCount.sixOrMore &&
			childInformationList.length > childrenCountMax() - 1
		) {
			setChildInformationList(childInformationList.slice(0, childrenCountMax()))
		}

		if (childrenCountInput.value === REQUEST_CONFIGS_KEYS.childCount.none) {
			for (let index = 0; index < childInformationList.length; index++) {
				removeChildInformationRow(index)
			}
		}

		onSetInputs(UpdateRequestInputsArray(inputs, childrenCountInput))
	}, [childrenCountInput])

	// SECTION A1
	const onSelectBeneficiaryFirstName = (value: string) => {
		setBeneficiaryFirstNameInput({
			...beneficiaryFirstNameInput,
			value
		})

		onFixError(
			UpdateRequestInputsErrorsArray(errors, beneficiaryFirstNameInput.name)
		)
	}

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, beneficiaryFirstNameInput))
	}, [beneficiaryFirstNameInput])

	const onSelectBeneficiaryLastName = (value: string) => {
		setBeneficiaryLastNameInput({
			...beneficiaryLastNameInput,
			value
		})

		onFixError(
			UpdateRequestInputsErrorsArray(errors, beneficiaryLastNameInput.name)
		)
	}

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, beneficiaryLastNameInput))
	}, [beneficiaryLastNameInput])

	const onSelectBeneficiaryAddress = (value: string) => {
		setBeneficiaryAddressInput({ ...beneficiaryAddressInput, value })

		onFixError(
			UpdateRequestInputsErrorsArray(errors, beneficiaryAddressInput.name)
		)
	}

	const onSelectBeneficiaryAppartment = (value: string) => {
		setBeneficiaryAppartmentInput({ ...beneficiaryAppartmentInput, value })

		onFixError(
			UpdateRequestInputsErrorsArray(errors, beneficiaryAppartmentInput.name)
		)
	}

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, beneficiaryAddressInput))
	}, [beneficiaryAddressInput])

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, beneficiaryAppartmentInput))
	}, [beneficiaryAppartmentInput])

	const onSelectBeneficiaryEmail = (value: string) => {
		setBeneficiaryEmailInput({
			...beneficiaryEmailInput,
			value
		})

		onFixError(
			UpdateRequestInputsErrorsArray(errors, beneficiaryEmailInput.name)
		)
	}

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, beneficiaryEmailInput))
	}, [beneficiaryEmailInput])

	const onSelectBeneficiaryPhoneNumber = (value: string) => {
		setBeneficiaryPhoneNumberInput({
			...beneficiaryPhoneNumberInput,
			value
		})

		onFixError(
			UpdateRequestInputsErrorsArray(errors, beneficiaryPhoneNumberInput.name)
		)
	}

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, beneficiaryPhoneNumberInput))
	}, [beneficiaryPhoneNumberInput])

	const onSelectBeneficiaryAge = (value: string) => {
		if (value.length > 2 || Number.isNaN(value)) {
			return
		}

		setBeneficiaryAgeInput({
			...beneficiaryAgeInput,
			value: `${value}`
		})

		onFixError(UpdateRequestInputsErrorsArray(errors, beneficiaryAgeInput.name))
	}

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, beneficiaryAgeInput))
	}, [beneficiaryAgeInput])

	const onSelectBeneficiarySex = (value: string) => {
		setBeneficiarySexInput({
			...beneficiarySexInput,
			value: `${value}`
		})

		onFixError(UpdateRequestInputsErrorsArray(errors, beneficiarySexInput.name))
	}

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, beneficiarySexInput))
	}, [beneficiarySexInput])

	// SECTION A2
	const onSelectSpouseFirstName = (value: string) => {
		setSpouseFirstNameInput({
			...spouseFirstNameInput,
			value
		})

		onFixError(
			UpdateRequestInputsErrorsArray(errors, spouseFirstNameInput.name)
		)
	}

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, spouseFirstNameInput))
	}, [spouseFirstNameInput])

	const onSelectSpouseLastName = (value: string) => {
		setSpouseLastNameInput({
			...spouseLastNameInput,
			value
		})

		onFixError(UpdateRequestInputsErrorsArray(errors, spouseLastNameInput.name))
	}

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, spouseLastNameInput))
	}, [spouseLastNameInput])

	const onSelectSpouseEmail = (value: string) => {
		setSpouseEmailInput({
			...spouseEmailInput,
			value
		})

		onFixError(UpdateRequestInputsErrorsArray(errors, spouseEmailInput.name))
	}

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, spouseEmailInput))
	}, [spouseEmailInput])

	const onSelectSpousePhoneNumber = (value: string) => {
		setSpousePhoneNumberInput({
			...spousePhoneNumberInput,
			value
		})

		onFixError(
			UpdateRequestInputsErrorsArray(errors, spousePhoneNumberInput.name)
		)
	}

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, spousePhoneNumberInput))
	}, [spousePhoneNumberInput])

	const onSelectSpouseAge = (value: string) => {
		if (value.length > 2 || Number.isNaN(value)) {
			return
		}

		setSpouseAgeInput({
			...spouseAgeInput,
			value: `${value}`
		})

		onFixError(UpdateRequestInputsErrorsArray(errors, spouseAgeInput.name))
	}

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, spouseAgeInput))
	}, [spouseAgeInput])

	const onSelectSpouseSex = (value: string) => {
		setSpouseSexInput({
			...spouseSexInput,
			value: `${value}`
		})

		onFixError(UpdateRequestInputsErrorsArray(errors, spouseSexInput.name))
	}

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, spouseSexInput))
	}, [spouseSexInput])

	// A3
	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, childInformationListInput))
	}, [childInformationListInput])

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, childAttributesInput))
	}, [childAttributesInput])

	// FILES
	const onSelectBeneficiaryFilesInput = (files: Set<File>) => {
		setBeneficiaryFilesInput({
			...beneficiaryFilesInput,
			files
		})

		onFixError(
			UpdateRequestInputsErrorsArray(errors, beneficiaryFilesInput.name)
		)
	}

	useEffect(() => {
		onSetInputFiles((prev) =>
			UpdateRequestInputFilesArray(prev, beneficiaryFilesInput)
		)
	}, [beneficiaryFilesInput])

	const onSelectSpouseFilesInput = (files: Set<File>) => {
		setSpouseFilesInput({
			...spouseFilesInput,
			files
		})

		onFixError(UpdateRequestInputsErrorsArray(errors, spouseFilesInput.name))
	}

	useEffect(() => {
		onSetInputFiles((prev) =>
			UpdateRequestInputFilesArray(prev, spouseFilesInput)
		)
	}, [spouseFilesInput])

	const onSelectOptionalFilesInput = (files: Set<File>) => {
		setOptionalFilesInput({
			...optionalFilesInput,
			files
		})

		onFixError(UpdateRequestInputsErrorsArray(errors, optionalFilesInput.name))
	}

	useEffect(() => {
		onSetInputFiles((prev) =>
			UpdateRequestInputFilesArray(prev, optionalFilesInput)
		)
	}, [optionalFilesInput])

	const onSelectProofOfResidencyInput = (files: Set<File>) => {
		setproofOfResidencyInput({
			...proofOfResidencyInput,
			files
		})

		onFixError(
			UpdateRequestInputsErrorsArray(errors, proofOfResidencyInput.name)
		)
	}

	useEffect(() => {
		onSetInputFiles((prev) =>
			UpdateRequestInputFilesArray(prev, proofOfResidencyInput)
		)
	}, [proofOfResidencyInput])

	useEffect(() => {
		// check if exist answers (filesInputs) at the store, to intialize the input with the files already present
		// ----------------------------
		// firstInput input
		if (!!answers.proofOfResidency && proofOfResidencyInput.files.size == 0) {
			getFilesFromStore('proofOfResidency', answers).then(
				(result: Set<File>) => {
					setproofOfResidencyInput({
						...proofOfResidencyInput,
						files: result
					})
				}
			)
		}
		if (!!answers.optionalFilesInput && optionalFilesInput.files.size == 0) {
			getFilesFromStore('o', answers).then((result: Set<File>) => {
				setOptionalFilesInput({
					...optionalFilesInput,
					files: result
				})
			})
		}
		if (
			!!answers.beneficiaryFilesInput &&
			beneficiaryFilesInput.files.size == 0
		) {
			getFilesFromStore('noticeOfContribution', answers).then(
				(result: Set<File>) => {
					setBeneficiaryFilesInput({
						...beneficiaryFilesInput,
						files: result
					})
				}
			)
		}
		if (!!answers.spouseFilesInput && spouseFilesInput.files.size == 0) {
			getFilesFromStore('noticeOfContribution', answers).then(
				(result: Set<File>) => {
					setSpouseFilesInput({
						...spouseFilesInput,
						files: result
					})
				}
			)
		}
	}, [])

	const onSetErrorFromFileComponent = (
		hasErrorInComponent: boolean,
		inputName: string
	) => {
		if (hasErrorInComponent) {
			if (!errors.includes(inputName)) {
				onFixError((oldErrors) => [...oldErrors, inputName])
			}

			return
		}

		onFixError(UpdateRequestInputsErrorsArray(errors, inputName))
	}

	useEffect(() => {
		onSetSubSteps({
			map: false,
			details_1: true,
			requiredDocument: true,
			position:
				subSteps?.position && String(subSteps?.position) !== ''
					? subSteps?.position
					: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1,
			steps: [
				REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1,
				REQUEST_TN_DETAILS_STEPS_NAME.DOCUMENTS
			]
		})
	}, [])

	const backToForm = (step: number, subStep: string) => {
		if (setCurrentStep && onSetSubSteps && subSteps) {
			setCurrentStep(step)
			onSetSubSteps({
				...subSteps,
				position: subStep as REQUEST_TN_DETAILS_STEPS_NAME
			})
		}
	}

	const renderChildInformation = (
		childInformation: ChildInformation,
		row: number
	) => {
		// Renders a single child information block (first name, last name, age, gender)
		// Each input id is suffixed with the row index so validation and error
		// messages can be associated with the correct child.
		return (
			<>
				<section style={{ border: '1px solid #ccc', padding: '20px' }}>
					<h6 style={{ display: 'inline' }} className={pageUtils.classes.h6}>
						{formatStrapiText(
							row > 0
								? row +
										1 +
										pageAssets?.request_form_tarifsReduction_numberChild_label
								: pageAssets?.request_form_sustainableHygiene_firstChild_label
						)}
					</h6>
					<button
						type="button"
						style={{ marginLeft: '4px' }}
						onClick={() => removeChildInformationRow(row)}
						className={joinClasses([
							classes.button,
							classes.outlinedButton,
							classes.btnPadding
						])}
					>
						{pageAssets?.request_form_LandDevelopmentWork_removeButton}
					</button>
					<div className={pageUtils.classes.fieldsContainer}>
						<TextInput
							id={`childInformation_firstName${row}`}
							label={spouseFirstNameInput.label}
							value={childInformation.firstName}
							onChange={(e) =>
								onSetChildInformationInput(e, {
									row,
									attribute: 'firstName'
								})
							}
							hasError={errors?.includes(`childInformation_firstName${row}`)}
							required
							classWrapper={pageUtils?.classes.textFieldContainer}
							maxCharacters={50}
						/>
						<TextInput
							id={`childInformation_lastName${row}`}
							label={spouseLastNameInput.label}
							value={childInformation.lastName}
							onChange={(e) =>
								onSetChildInformationInput(e, {
									row,
									attribute: 'lastName'
								})
							}
							hasError={errors?.includes(`childInformation_lastName${row}`)}
							required
							classWrapper={pageUtils?.classes.textFieldContainer}
							maxCharacters={50}
						/>

						<TextInput
							id={`childInformation_age${row}`}
							label={spouseAgeInput.label}
							value={childInformation.age}
							onChange={(e) =>
								onSetChildInformationInput(e, {
									row,
									attribute: 'age'
								})
							}
							hasError={errors?.includes(`childInformation_age${row}`)}
							onKeyPress={pageUtils.onKeyPressIntegerInput}
							required
							type="number"
						/>

						<SelectInput
							id={`childInformation_gender${row}`}
							label={spouseSexInput.label}
							value={childInformation.gender || ''}
							onChange={(e) =>
								onSetChildInformationInput(e, {
									row,
									attribute: 'gender'
								})
							}
							hasError={errors?.includes(`childInformation_gender${row}`)}
							list={radioListGender}
						/>
					</div>
				</section>
			</>
		)
	}

	const getLabelFromList = (list, value) => {
		// Utility: find a localized label from a Radio/List by matching its value
		return list?.find((element) => element.value == value?.toString())?.label
	}

	const isBeneficiary = (value) => {
		// Short helper used to decide which set of beneficiary inputs/labels to show
		return (
			value.toString() === REQUEST_CONFIGS_KEYS.carSharingForWhom.secondOption
		)
	}

	// Summary / print view
	// When isSummaryStep is true the component renders a read-only summary of
	// the previously entered values (answers from the store) and the uploaded
	// files. Interactive inputs are hidden but a "modify" link is provided to
	// jump back to the form sub-step.
	if (isSummaryStep) {
		return (
			<section
				className={pageUtils.classes.noMargin}
				style={{ padding: '0px' }}
			>
				{subSteps?.details_1 && (
					<div>
						<button
							type="button"
							style={{ display: toPrint ? 'none' : 'flex', float: 'right' }}
							className={joinClasses([
								pageUtils.classes.btn,
								pageUtils.classes.button,
								pageUtils.classes.buttonOutline
							])}
							onClick={() =>
								backToForm(
									steps?.form!,
									REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1
								)
							}
						>
							{pageAssets?.label_modify}
						</button>
						<h4 className={pageUtils.classes.h4}>
							{pageAssets?.request_form_leisureTarifs_firstSubStep_title}
						</h4>

						<div>
							<strong>
								{pageAssets?.request_form_sustainableHygiene_forWhom_label}
							</strong>
							<br />
							<span className={pageUtils.classes.answer}>
								{getLabelFromList(
									radioListGrantForWhom,
									answers?.grantForWhom
								)}
							</span>
						</div>

						<div>
							<strong>
								{isBeneficiary(answers?.grantForWhom)
									? pageAssets?.request_form_leisureTarifs_maritalStatus_label
									: pageAssets?.request_form_leisureTarifs_maritalStatus_self_label}
							</strong>
							<br />
							<span className={pageUtils.classes.answer}>
								{getLabelFromList(
									radioListMaritalStatus,
									answers?.maritalStatus
								)}
							</span>
						</div>

						<div>
							<strong>
								{isBeneficiary(answers?.grantForWhom)
									? pageAssets?.request_form_leisureTarifs_childrenCount_label
									: pageAssets?.request_form_leisureTarifs_childrenCount_self_label}
							</strong>
							<br />
							<span className={pageUtils.classes.answer}>
								{getLabelFromList(
									radioListChildrenCount,
									answers?.childrenCount
								)}
							</span>
						</div>
						<h6 className={pageUtils.classes.h6}>
							{pageAssets?.request_form_leisureTarifs_included_people_title}
						</h6>

						<strong>
							{isBeneficiary(answers?.grantForWhom)
								? pageAssets?.request_form_leisureTarifs_beneficiary_information_title
								: pageAssets?.request_form_leisureTarifs_beneficiary_self_information_title}
						</strong>

						<div style={{ marginBottom: '15px', lineHeight: '22px' }}>
							<div className={pageUtils.classes.fieldsContainer}>
								{answers?.grantForWhom?.toString() ===
								REQUEST_CONFIGS_KEYS.carSharingForWhom.firstOption ? (
									<>
										<span>
											{authUser?.profile?.firstName +
												` ` +
												authUser?.profile?.lastName}
										</span>
										<br />
										<div>
											<strong>{pageAssets?.label_email}</strong>
											<br />
											<span>{authUser?.profile?.email}</span>
										</div>
										<div>
											<strong>{pageAssets?.label_telephone}</strong>
											<br />
											<span>{authUser?.profile?.phoneNumber}</span>
										</div>
									</>
								) : (
									<>
										<span>
											{answers?.beneficiaryFirstName +
												` ` +
												answers?.beneficiaryLastName}
										</span>
										<br />
										{answers?.beneficiaryAddress && (
											<div>
												<strong>
													{pageAssets?.request_form_sustainableHygiene_beneficiary_address}
												</strong>
												<br />
												<span>{answers.beneficiaryAddress}</span>
											</div>
										)}
										{answers?.beneficiaryAppartment && (
											<div>
												<strong>{pageAssets?.apartment_label}</strong>
												<br />
												<span>{answers.beneficiaryAppartment}</span>
											</div>
										)}
										<div>
											<strong>{pageAssets?.label_email}</strong>
											<br />
											<span>{answers?.beneficiaryEmail}</span>
										</div>
										<div>
											<strong>{pageAssets?.label_telephone}</strong>
											<br />
											<span>{answers?.beneficiaryPhoneNumber}</span>
										</div>
									</>
								)}
								<div>
									<strong>{pageAssets?.label_age}</strong>
									<br />
									<span>{answers?.beneficiaryAge}</span>
								</div>
								{answers?.beneficiarySex && (
									<div>
										<strong>{pageAssets?.label_sex}</strong>
										<br />
										<span>
											{getLabelFromList(
												radioListGender,
												answers?.beneficiarySex
											)}
										</span>
									</div>
								)}
							</div>

							{beneficiaryFilesInput.files.size > 0 && (
								<RequestFiles
									subtitle={beneficiaryFilesInput.label}
									name={beneficiaryFilesInput.name}
									required={beneficiaryFilesInput.required}
									files={beneficiaryFilesInput.files}
									hasError={errors.includes(beneficiaryFilesInput.name)}
									removeSectionMarginTop
									onSetHasError={onSetErrorFromFileComponent}
									onSetFiles={onSelectBeneficiaryFilesInput}
									maxFiles={beneficiaryFilesInput.numberFilesRequired}
									isSummary
									removeSectionPaddingTop
								>
									<></>
								</RequestFiles>
							)}
						</div>

						{answers?.maritalStatus ==
							Number(REQUEST_CONFIGS_KEYS.maritalStatus.married) && (
							<>
								<strong>
									{
										pageAssets?.request_form_leisureTarifs_spouse_information_title
									}
								</strong>
								<div
									style={{ lineHeight: '22px' }}
									className={pageUtils.classes.fieldsContainer}
								>
									<span>
										{answers?.spouseFirstName + ` ` + answers?.spouseLastName}
									</span>
									<br />
									<div>
										<strong>{pageAssets?.label_email}</strong>
										<br />
										<span>{answers?.spouseEmail}</span>
									</div>
									<div>
										<strong>{pageAssets?.label_telephone}</strong>
										<br />
										<span>{answers?.spousePhoneNumber}</span>
									</div>
									<div>
										<strong>{pageAssets?.label_age}</strong>
										<br />
										<span>{answers?.spouseAge}</span>
									</div>
									{answers?.spouseSex && (
										<div>
											<strong>{pageAssets?.label_sex}</strong>
											<br />
											<span>
												{getLabelFromList(radioListGender, answers?.spouseSex)}
											</span>
										</div>
									)}
								</div>

								{spouseFilesInput.files.size > 0 && (
									<RequestFiles
										subtitle={spouseFilesInput.label}
										name={spouseFilesInput.name}
										required={spouseFilesInput.required}
										files={spouseFilesInput.files}
										hasError={errors.includes(spouseFilesInput.name)}
										removeSectionMarginTop
										onSetHasError={onSetErrorFromFileComponent}
										onSetFiles={onSelectSpouseFilesInput}
										maxFiles={spouseFilesInput.numberFilesRequired}
										isSummary
										removeSectionPaddingTop
									>
										<></>
									</RequestFiles>
								)}
							</>
						)}
						{answers?.childInformationList &&
							answers?.childInformationList?.length > 0 && (
								<>
									<h6 className={pageUtils.classes.h6}>
										{
											pageAssets?.request_form_sustainableHygiene_dependentChild_label
										}
									</h6>

									{answers?.childInformationList?.map((element, row) => (
										<>
											<h6
												className={pageUtils.classes.h6}
												style={{ fontSize: '16px', margin: '0px' }}
											>
												{formatStrapiText(
													row > 0
														? row +
																1 +
																pageAssets?.request_form_tarifsReduction_numberChild_label
														: pageAssets?.request_form_sustainableHygiene_firstChild_label
												)}
											</h6>
											<div
												style={{
													lineHeight: '22px',
													marginBottom: '10px'
												}}
												className={joinClasses([
													pageUtils.classes.fieldsContainer,
													classes.greyBackgroundBlock
												])}
											>
												<div>
													<strong>{pageAssets?.label_firstName}</strong>
													<br />
													<span>{element?.firstName}</span>
												</div>

												<div>
													<strong>{pageAssets?.label_lastName}</strong>
													<br />
													<span>{element?.lastName}</span>
												</div>

												<div>
													<strong>{pageAssets?.label_age}</strong>
													<br />
													<span>{element?.age}</span>
												</div>
												{element?.gender && (
													<div>
														<strong>{pageAssets?.label_sex}</strong>
														<br />
														<span>
															{getLabelFromList(
																radioListGender,
																element?.gender
															)}
														</span>
													</div>
												)}
											</div>
										</>
									))}
								</>
							)}
					</div>
				)}

				{subSteps?.requiredDocument && (
					<>
						{(proofOfResidencyInput.files.size > 0 ||
							optionalFilesInput.files.size > 0) && (
							<div style={{ marginTop: '40px' }}>
								<button
									type="button"
									style={{ display: toPrint ? 'none' : 'flex', float: 'right' }}
									className={joinClasses([
										pageUtils.classes.btn,
										pageUtils.classes.button,
										pageUtils.classes.buttonOutline
									])}
									onClick={() =>
										backToForm(
											steps?.form!,
											REQUEST_TN_DETAILS_STEPS_NAME.DOCUMENTS
										)
									}
								>
									{pageAssets?.label_modify}
								</button>

								<h4 className={pageUtils.classes.h4}>
									{pageAssets?.request_form_required_documents}
								</h4>
							</div>
						)}

						{proofOfResidencyInput.files.size > 0 && (
							<RequestFiles
								subtitle={proofOfResidencyInput.label}
								name={proofOfResidencyInput.name}
								required={proofOfResidencyInput.required}
								files={proofOfResidencyInput.files}
								hasError={errors.includes(proofOfResidencyInput.name)}
								removeSectionMarginTop
								onSetHasError={onSetErrorFromFileComponent}
								onSetFiles={onSelectProofOfResidencyInput}
								maxFiles={proofOfResidencyInput.numberFilesRequired}
								isSummary
								removeSectionPaddingTop
							>
								<></>
							</RequestFiles>
						)}

						{optionalFilesInput.files.size > 0 && (
							<RequestFiles
								subtitle={optionalFilesInput.label}
								name={optionalFilesInput.name}
								required={optionalFilesInput.required}
								files={optionalFilesInput.files}
								hasError={errors.includes(optionalFilesInput.name)}
								removeSectionMarginTop
								onSetHasError={onSetErrorFromFileComponent}
								onSetFiles={onSelectOptionalFilesInput}
								maxFiles={optionalFilesInput.numberMaxFiles}
								isSummary
							>
								<></>
							</RequestFiles>
						)}
					</>
				)}
			</section>
		)
	}

	return (
		<>
			<Collapse
				isOpened={
					subSteps !== undefined &&
					subSteps.position === REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1
				}
			>
				<section>
					<h4 className={pageUtils.classes.h4}>
						{pageAssets?.request_form_leisureTarifs_firstSubStep_title}
					</h4>

					<RadioInput
						name={grantForWhomInput.name}
						label={grantForWhomInput.label}
						value={grantForWhomInput.value}
						list={radioListGrantForWhom}
						onChange={onSelectGrantForWhomInput}
						hasError={errors.includes(grantForWhomInput.name)}
						required
						ref={grantForWhomInput.ref as RefObject<HTMLInputElement>}
						hasBackgroundWrapper={true}
					/>

					{isBeneficiary(grantForWhomInput.value) && (
						<Notification
							text={formatStrapiText(
								pageAssets?.request_form_leisureTarifs_grant_notification_title
							)}
							type="info"
							hasHtml
						/>
					)}
					{grantForWhomInput.value && (
						<>
							<h5 className={pageUtils.classes.h5}>
								{
									pageAssets?.request_form_leisureTarifs_family_composition_section_title
								}
							</h5>
							{/*SECTION A */}
							<RadioInput
								name={maritalStatusInput.name}
								label={
									isBeneficiary(grantForWhomInput.value)
										? maritalStatusInput.label
										: pageAssets?.request_form_leisureTarifs_maritalStatus_self_label
								}
								value={maritalStatusInput.value}
								list={radioListMaritalStatus}
								direction={Direction.vertical}
								hasError={errors.includes(maritalStatusInput.name)}
								onChange={onSelectMaritalStatusInput}
								ref={maritalStatusInput.ref as RefObject<HTMLInputElement>}
								required={maritalStatusInput.required}
								hasBackgroundWrapper
							/>
							<RadioInput
								name={childrenCountInput.name}
								label={
									isBeneficiary(grantForWhomInput.value)
										? childrenCountInput.label
										: pageAssets?.request_form_leisureTarifs_childrenCount_self_label
								}
								value={childrenCountInput.value}
								list={radioListChildrenCount}
								direction={Direction.vertical}
								hasError={errors.includes(childrenCountInput.name)}
								onChange={onSelectChildrenCountInput}
								ref={childrenCountInput.ref as RefObject<HTMLInputElement>}
								required={childrenCountInput.required}
								hasBackgroundWrapper
							/>

							{/**SECTION A1 */}
							{maritalStatusInput.value && (
								<>
									<h5 className={pageUtils.classes.h5}>
										{
											pageAssets?.request_form_leisureTarifs_included_people_title
										}
									</h5>

									<h6 className={pageUtils.classes.h6}>
										{isBeneficiary(grantForWhomInput.value)
											? pageAssets?.request_form_leisureTarifs_beneficiary_information_title
											: pageAssets?.request_form_leisureTarifs_beneficiary_self_information_title}
									</h6>

									<section
										style={{ border: '1px solid #ccc', padding: '20px' }}
									>
										{!isBeneficiary(grantForWhomInput.value) && (
											<div style={{ marginBottom: '15px', lineHeight: '22px' }}>
												<strong>
													{authUser?.profile?.firstName +
														` ` +
														authUser?.profile?.lastName}
												</strong>
												<br />
												<p>{authUser?.profile?.email}</p>
												<p>{authUser?.profile?.phoneNumber}</p>
											</div>
										)}
										<div className={pageUtils.classes.fieldsContainer}>
											{isBeneficiary(grantForWhomInput.value) && (
												<>
													<TextInput
														id={beneficiaryFirstNameInput.name}
														label={beneficiaryFirstNameInput.label}
														value={beneficiaryFirstNameInput.value}
														onChange={onSelectBeneficiaryFirstName}
														hasError={errors.includes(
															beneficiaryFirstNameInput.name
														)}
														required={beneficiaryFirstNameInput.required}
														ref={
															beneficiaryFirstNameInput.ref as RefObject<HTMLInputElement>
														}
														classWrapper={pageUtils?.classes.textFieldContainer}
														maxCharacters={50}
													/>
													<TextInput
														id={beneficiaryLastNameInput.name}
														label={beneficiaryLastNameInput.label}
														value={beneficiaryLastNameInput.value}
														onChange={onSelectBeneficiaryLastName}
														hasError={errors.includes(
															beneficiaryLastNameInput.name
														)}
														required={beneficiaryLastNameInput.required}
														ref={
															beneficiaryLastNameInput.ref as RefObject<HTMLInputElement>
														}
														classWrapper={pageUtils?.classes.textFieldContainer}
														maxCharacters={50}
													/>
													<TextInput
														id={beneficiaryEmailInput.name}
														label={beneficiaryEmailInput.label}
														value={beneficiaryEmailInput.value}
														onChange={onSelectBeneficiaryEmail}
														hasError={errors.includes(
															beneficiaryEmailInput.name
														)}
														required={beneficiaryEmailInput.required}
														ref={
															beneficiaryEmailInput.ref as RefObject<HTMLInputElement>
														}
														schema={formUtils.MASKS.email}
														schemaErrorText={pageAssets.label_emailErrorText}
														maxCharacters={50}
														type="email"
														onSetError={onFixError}
													/>
													<MaskedTextInput
														id={beneficiaryPhoneNumberInput.name}
														label={beneficiaryPhoneNumberInput.label}
														value={beneficiaryPhoneNumberInput.value}
														onChange={onSelectBeneficiaryPhoneNumber}
														hasError={errors?.includes(
															beneficiaryPhoneNumberInput.name
														)}
														type="tel"
														min={10}
														max={14}
														mask={formUtils.MASKS.phoneNumber}
														placeholder={formUtils.MASKS.phoneNumber}
														errors={errors ?? []}
														setError={onFixError}
														schema={PHONE_NUMBER_REGEX}
														required={beneficiaryPhoneNumberInput.required}
														ref={
															beneficiaryPhoneNumberInput.ref as RefObject<HTMLInputElement>
														}
													/>
												</>
											)}
										</div>

										{isBeneficiary(grantForWhomInput.value) && (
											<MapForm
												addressLabel={beneficiaryAddressInput.label}
												addressRequired={beneficiaryAddressInput.required}
												displayAptInput
												value={answers?.beneficiaryAddress}
												hasError={errors?.includes(beneficiaryAddressInput.name)}
												onAddressInputChange={onSelectBeneficiaryAddress}
												onApartmentInputChange={onSelectBeneficiaryAppartment}
												addressLabelNoWrapper
											/>
										)}

										<div className={pageUtils.classes.fieldsContainer}>
											<TextInput
												id={beneficiaryAgeInput.name}
												label={beneficiaryAgeInput.label}
												value={beneficiaryAgeInput.value}
												onChange={onSelectBeneficiaryAge}
												hasError={errors.includes(beneficiaryAgeInput.name)}
												required={beneficiaryAgeInput.required}
												ref={
													beneficiaryAgeInput.ref as RefObject<HTMLInputElement>
												}
												maxCharacters={50}
												min="1"
												onKeyPress={pageUtils.onKeyPressIntegerInput}
												type="number"
												onSetError={onFixError}
											/>

											<SelectInput
												id={beneficiarySexInput.name}
												label={beneficiarySexInput.label}
												value={beneficiarySexInput.value}
												list={radioListGender}
												hasError={errors.includes(beneficiarySexInput.name)}
												onChange={onSelectBeneficiarySex}
												ref={
													beneficiarySexInput.ref as RefObject<HTMLSelectElement>
												}
												required={beneficiarySexInput.required}
											/>
										</div>
										<RequestFiles
											subtitle={beneficiaryFilesInput.label}
											name={beneficiaryFilesInput.name}
											required={beneficiaryFilesInput.required}
											files={beneficiaryFilesInput.files}
											hasError={errors.includes(beneficiaryFilesInput.name)}
											onSetHasError={onSetErrorFromFileComponent}
											onSetFiles={onSelectBeneficiaryFilesInput}
											maxFiles={proofOfResidencyInput.numberFilesRequired}
											description={beneficiaryFilesInput.description}
											removePadding
											removeSectionMarginTop
										>
											<></>
										</RequestFiles>
									</section>
								</>
							)}
							{/**SECTION A2 */}
							{maritalStatusInput.value ===
								REQUEST_CONFIGS_KEYS.maritalStatus.married && (
								<>
									<h6 className={pageUtils.classes.h6}>
										{
											pageAssets?.request_form_leisureTarifs_spouse_information_title
										}
									</h6>

									<section
										style={{ border: '1px solid #ccc', padding: '20px' }}
									>
										<div className={pageUtils.classes.fieldsContainer}>
											<TextInput
												id={spouseFirstNameInput.name}
												label={spouseFirstNameInput.label}
												value={spouseFirstNameInput.value}
												onChange={onSelectSpouseFirstName}
												hasError={errors.includes(spouseFirstNameInput.name)}
												required={spouseFirstNameInput.required}
												ref={
													spouseFirstNameInput.ref as RefObject<HTMLInputElement>
												}
												classWrapper={pageUtils?.classes.textFieldContainer}
												maxCharacters={50}
											/>
											<TextInput
												id={spouseLastNameInput.name}
												label={spouseLastNameInput.label}
												value={spouseLastNameInput.value}
												onChange={onSelectSpouseLastName}
												hasError={errors.includes(spouseLastNameInput.name)}
												required={spouseLastNameInput.required}
												ref={
													spouseLastNameInput.ref as RefObject<HTMLInputElement>
												}
												classWrapper={pageUtils?.classes.textFieldContainer}
												maxCharacters={50}
											/>

											<TextInput
												id={spouseEmailInput.name}
												label={spouseEmailInput.label}
												value={spouseEmailInput.value}
												onChange={onSelectSpouseEmail}
												hasError={errors.includes(spouseEmailInput.name)}
												required={spouseEmailInput.required}
												ref={
													spouseEmailInput.ref as RefObject<HTMLInputElement>
												}
												schema={formUtils.MASKS.email}
												schemaErrorText={pageAssets.label_emailErrorText}
												maxCharacters={50}
												type="email"
												onSetError={onFixError}
											/>

											<MaskedTextInput
												id={spousePhoneNumberInput.name}
												label={spousePhoneNumberInput.label}
												value={spousePhoneNumberInput.value}
												onChange={onSelectSpousePhoneNumber}
												hasError={errors?.includes(spousePhoneNumberInput.name)}
												type="tel"
												min={10}
												max={14}
												mask={formUtils.MASKS.phoneNumber}
												placeholder={formUtils.MASKS.phoneNumber}
												errors={errors ?? []}
												setError={onFixError}
												schema={PHONE_NUMBER_REGEX}
												required={spousePhoneNumberInput.required}
												ref={
													spousePhoneNumberInput.ref as RefObject<HTMLInputElement>
												}
											/>

											<TextInput
												id={spouseAgeInput.name}
												label={spouseAgeInput.label}
												value={spouseAgeInput.value}
												onChange={onSelectSpouseAge}
												hasError={errors.includes(spouseAgeInput.name)}
												required={spouseAgeInput.required}
												ref={spouseAgeInput.ref as RefObject<HTMLInputElement>}
												maxCharacters={50}
												onKeyPress={pageUtils.onKeyPressIntegerInput}
												min="1"
												type="number"
												onSetError={onFixError}
											/>

											<SelectInput
												id={spouseSexInput.name}
												label={spouseSexInput.label}
												value={spouseSexInput.value}
												list={radioListGender}
												hasError={errors.includes(spouseSexInput.name)}
												onChange={onSelectSpouseSex}
												ref={spouseSexInput.ref as RefObject<HTMLSelectElement>}
												required={spouseSexInput.required}
											/>
										</div>
										<RequestFiles
											subtitle={spouseFilesInput.label}
											name={spouseFilesInput.name}
											required={spouseFilesInput.required}
											files={spouseFilesInput.files}
											hasError={errors.includes(spouseFilesInput.name)}
											onSetHasError={onSetErrorFromFileComponent}
											onSetFiles={onSelectSpouseFilesInput}
											maxFiles={proofOfResidencyInput.numberFilesRequired}
											description={spouseFilesInput.description}
											removePadding
										>
											<></>
										</RequestFiles>
									</section>
								</>
							)}
							{/**SECTION A3 */}
							{!['', REQUEST_CONFIGS_KEYS.childCount.none].includes(
								childrenCountInput.value
							) && (
								<>
									<h6 className={pageUtils.classes.h6}>
										{
											pageAssets?.request_form_sustainableHygiene_dependentChild_label
										}
									</h6>
									<p>
										{
											pageAssets?.request_form_leisureTarifs_children_description_title
										}
									</p>
									{childInformationList?.map((childInformation, index) => {
										return renderChildInformation(childInformation, index)
									})}

									{childInformationList.length < childrenCountMax() && (
											<button
												type="button"
												style={{ margin: '20px 0px 12px' }}
												onClick={() => addChildInformationRow()}
												className={joinClasses([
													classes.button,
													classes.btnPadding
												])}
											>
												{pageAssets?.label_add_child}
											</button>
									)}
								</>
							)}
						</>
					)}
				</section>
			</Collapse>

			<Collapse
				isOpened={
					subSteps !== undefined &&
					subSteps.position === REQUEST_TN_DETAILS_STEPS_NAME.DOCUMENTS
				}
			>
				<section className={pageUtils.classes.sectionNoPadding}>
					<h4 className={pageUtils.classes.h4}>
						{pageAssets?.request_form_required_documents}
					</h4>

					<span className={pageUtils.classes.fileInfo}>
						{pageAssets?.page_requestInfo_inputFileDesc}
					</span>

					<RequestFiles
						subtitle={proofOfResidencyInput.label}
						name={proofOfResidencyInput.name}
						required={proofOfResidencyInput.required}
						files={proofOfResidencyInput.files}
						hasError={errors.includes(proofOfResidencyInput.name)}
						description={proofOfResidencyInput.description}
						removeSectionMarginTop
						onSetHasError={onSetErrorFromFileComponent}
						onSetFiles={onSelectProofOfResidencyInput}
						maxFiles={proofOfResidencyInput.numberFilesRequired}
					>
						<></>
					</RequestFiles>

					<RequestFiles
						subtitle={optionalFilesInput.label}
						name={optionalFilesInput.name}
						required={optionalFilesInput.required}
						files={optionalFilesInput.files}
						hasError={errors.includes(optionalFilesInput.name)}
						description={optionalFilesInput.description}
						removeSectionMarginTop
						onSetHasError={onSetErrorFromFileComponent}
						onSetFiles={onSelectOptionalFilesInput}
						maxFiles={optionalFilesInput.numberMaxFiles}
					>
						<></>
					</RequestFiles>
				</section>
			</Collapse>
		</>
	)
}

export default LeisureTarifsReductionForm
